import React, { useState } from 'react';
import { Checkbox } from '../../../components/ui/Checkbox';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const ReviewStep = ({ formData, onSubmit, onBack }) => {
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [agreedToPrivacy, setAgreedToPrivacy] = useState(false);
  const [agreedToRisks, setAgreedToRisks] = useState(false);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    const newErrors = {};

    if (!agreedToTerms) {
      newErrors.terms = 'You must agree to the Terms of Service';
    }
    if (!agreedToPrivacy) {
      newErrors.privacy = 'You must agree to the Privacy Policy';
    }
    if (!agreedToRisks) {
      newErrors.risks = 'You must acknowledge the investment risks';
    }

    if (Object.keys(newErrors)?.length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsSubmitting(true);
    await new Promise(resolve => setTimeout(resolve, 2000));
    onSubmit();
  };

  const InfoSection = ({ icon, title, children, iconColor }) => (
    <div className="bg-[var(--color-card)] rounded-lg p-4 md:p-6 border border-[var(--color-border)]">
      <div className="flex items-start gap-3 mb-4">
        <div className={`w-10 h-10 md:w-12 md:h-12 rounded-lg flex items-center justify-center flex-shrink-0`} style={{ backgroundColor: `${iconColor}15` }}>
          <Icon name={icon} size={20} color={iconColor} />
        </div>
        <div className="flex-1">
          <h4 className="text-base md:text-lg font-semibold text-[var(--color-foreground)]">
            {title}
          </h4>
        </div>
      </div>
      {children}
    </div>
  );

  const DataRow = ({ label, value }) => (
    <div className="flex flex-col sm:flex-row sm:items-center py-3 border-b border-[var(--color-border)] last:border-0">
      <span className="text-sm text-[var(--color-muted-foreground)] sm:w-1/3 mb-1 sm:mb-0">
        {label}
      </span>
      <span className="text-sm md:text-base font-medium text-[var(--color-foreground)] sm:w-2/3">
        {value || 'Not provided'}
      </span>
    </div>
  );

  return (
    <form onSubmit={handleSubmit} className="space-y-4 md:space-y-5 lg:space-y-6">
      <div className="bg-[var(--color-accent)]/10 border border-[var(--color-accent)]/20 rounded-lg p-4 md:p-6">
        <div className="flex gap-3">
          <Icon name="AlertCircle" size={24} color="var(--color-accent)" className="flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-base md:text-lg font-semibold text-[var(--color-foreground)] mb-2">
              Review Your Information
            </h3>
            <p className="text-sm md:text-base text-[var(--color-muted-foreground)]">
              Please carefully review all information before submitting. Once submitted, some details cannot be changed without contacting support.
            </p>
          </div>
        </div>
      </div>
      <InfoSection icon="User" title="Personal Information" iconColor="var(--color-primary)">
        <div className="space-y-0">
          <DataRow label="Full Name" value={`${formData?.firstName} ${formData?.lastName}`} />
          <DataRow label="Email" value={formData?.email} />
          <DataRow label="Phone" value={formData?.phone} />
          <DataRow label="Date of Birth" value={formData?.dateOfBirth} />
          <DataRow label="Country" value={formData?.country?.toUpperCase()} />
        </div>
      </InfoSection>
      <InfoSection icon="MapPin" title="Address Information" iconColor="var(--color-secondary)">
        <div className="space-y-0">
          <DataRow label="Street Address" value={formData?.address} />
          <DataRow label="City" value={formData?.city} />
          <DataRow label="Postal Code" value={formData?.postalCode} />
        </div>
      </InfoSection>
      <InfoSection icon="Shield" title="Identity Verification" iconColor="var(--color-primary)">
        <div className="space-y-0">
          <DataRow label="Driver's License (Front)" value={formData?.driversLicenseFront ? 'Uploaded' : 'Not provided'} />
          <DataRow label="Driver's License (Back)" value={formData?.driversLicenseBack ? 'Uploaded' : 'Not provided'} />
          <DataRow label="Social Security Number" value={formData?.ssn ? '•••-••-' + formData?.ssn?.slice(-4) : 'Not provided'} />
          <DataRow label="Father's Middle Name" value={formData?.fatherMiddleName} />
          <DataRow label="Mother's Middle Name" value={formData?.motherMiddleName} />
          <DataRow label="Place of Birth" value={formData?.placeOfBirth} />
        </div>
      </InfoSection>
      <InfoSection icon="Lock" title="Security Settings" iconColor="var(--color-accent)">
        <div className="space-y-0">
          <DataRow label="Password" value="••••••••••••" />
          <DataRow label="Security Question" value={formData?.securityQuestion} />
          <DataRow label="Two-Factor Auth" value={formData?.enable2FA ? 'Enabled' : 'Disabled'} />
        </div>
      </InfoSection>
      <div className="bg-[var(--color-card)] rounded-lg p-4 md:p-6 lg:p-8 border border-[var(--color-border)]">
        <div className="flex items-start gap-3 mb-4 md:mb-5 lg:mb-6">
          <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg bg-[var(--color-error)]/10 flex items-center justify-center flex-shrink-0">
            <Icon name="FileText" size={20} color="var(--color-error)" />
          </div>
          <div>
            <h3 className="text-lg md:text-xl lg:text-2xl font-semibold text-[var(--color-foreground)]">
              Terms &amp; Agreements
            </h3>
            <p className="text-sm md:text-base text-[var(--color-muted-foreground)] mt-1">
              Please read and accept all agreements to continue
            </p>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <Checkbox
              label={
                <span className="text-sm md:text-base">
                  I agree to the{' '}
                  <a href="#" className="text-[var(--color-primary)] hover:underline">
                    Terms of Service
                  </a>
                </span>
              }
              description="By checking this box, you agree to our terms and conditions"
              checked={agreedToTerms}
              onChange={(e) => {
                setAgreedToTerms(e?.target?.checked);
                if (errors?.terms) setErrors({ ...errors, terms: '' });
              }}
            />
            {errors?.terms && (
              <p className="text-xs md:text-sm text-[var(--color-error)] flex items-center gap-1 mt-1">
                <Icon name="AlertCircle" size={14} />
                {errors?.terms}
              </p>
            )}
          </div>

          <div>
            <Checkbox
              label={
                <span className="text-sm md:text-base">
                  I agree to the{' '}
                  <a href="#" className="text-[var(--color-primary)] hover:underline">
                    Privacy Policy
                  </a>
                </span>
              }
              description="We respect your privacy and protect your personal information"
              checked={agreedToPrivacy}
              onChange={(e) => {
                setAgreedToPrivacy(e?.target?.checked);
                if (errors?.privacy) setErrors({ ...errors, privacy: '' });
              }}
            />
            {errors?.privacy && (
              <p className="text-xs md:text-sm text-[var(--color-error)] flex items-center gap-1 mt-1">
                <Icon name="AlertCircle" size={14} />
                {errors?.privacy}
              </p>
            )}
          </div>

          <div>
            <Checkbox
              label={
                <span className="text-sm md:text-base font-medium">
                  I acknowledge the risks of cryptocurrency investment
                </span>
              }
              description="Cryptocurrency investments carry significant risk. You may lose some or all of your investment."
              checked={agreedToRisks}
              onChange={(e) => {
                setAgreedToRisks(e?.target?.checked);
                if (errors?.risks) setErrors({ ...errors, risks: '' });
              }}
            />
            {errors?.risks && (
              <p className="text-xs md:text-sm text-[var(--color-error)] flex items-center gap-1 mt-1">
                <Icon name="AlertCircle" size={14} />
                {errors?.risks}
              </p>
            )}
          </div>
        </div>

        <div className="mt-6 bg-[var(--color-warning)]/10 border border-[var(--color-warning)]/20 rounded-lg p-4">
          <div className="flex gap-3">
            <Icon name="AlertTriangle" size={20} color="var(--color-warning)" className="flex-shrink-0 mt-0.5" />
            <div className="text-xs md:text-sm text-[var(--color-foreground)]">
              <p className="font-medium mb-1">Important Risk Disclosure</p>
              <p className="text-[var(--color-muted-foreground)]">
                Cryptocurrency trading involves substantial risk of loss. Past performance does not guarantee future results. Only invest what you can afford to lose.
              </p>
            </div>
          </div>
        </div>
      </div>
      <div className="flex flex-col sm:flex-row gap-3 pt-4">
        <Button
          type="button"
          variant="outline"
          size="lg"
          iconName="ArrowLeft"
          iconPosition="left"
          onClick={onBack}
          disabled={isSubmitting}
          className="sm:w-auto"
        >
          Back
        </Button>
        <Button
          type="submit"
          variant="default"
          size="lg"
          iconName="CheckCircle"
          iconPosition="right"
          loading={isSubmitting}
          className="sm:flex-1"
        >
          {isSubmitting ? 'Creating Account...' : 'Complete Registration'}
        </Button>
      </div>
    </form>
  );
};

export default ReviewStep;